/* eslint-disable */
import { memo, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Container, Stack } from '@mui/material';
import PositiveButton from 'components/cmn/PositiveButton';
import useInspection from 'hooks/useInspection';
import { IClassificationProps, IFormProps } from 'interface/inspection/InspectionInfo';
import useInputs from 'hooks/useInputs';
import CheckGroup from 'components/inspection/CheckGroup';
import { useAppDispatch } from 'store';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import { postInspectionResult, postInspectionClickLogs } from 'store/inspection/inspectionSlice';
import { useProject } from 'hooks/useProject';
import { showMessage } from 'store/message/messageSlice';
import { inspectionLogger, getStepName } from 'utils/inspectionLogger';

function ClassificationFinalCheckN(): JSX.Element | null {
  const {
    inspectionFinalBase,
    inspectionEndClassification,
    // inspectionFinalStandard, // 제거됨 - 새로 만들 예정
    // inspectionFinalTmp, // 제거됨 - 새로 만들 예정
    surveyAdd,
    inspectionDetail,
    // changeInspectionFinalTmp, // 제거됨 - 새로 만들 예정
    autoFillInspectionFinalBase,
    removeItemsFromInspectionEndClassification,
    changeInspectionFinalBase,
    // changeInspectionFinalTmpObject, // 제거됨 - 새로 만들 예정
    changeClassification,
    // clearInspectionFinalBaseArrays, // 제거됨 - 새로 만들 예정
  } = useInspection();

  // TODO: 새로 만들 예정 - 임시로 빈 배열 사용
  const inspectionFinalStandard: any[] = [];
  const inspectionFinalTmp: any[] = [];
  const [k, setK] = useState(1); // 오른쪽부터 시작 (1번방)
  const [m, setM] = useState(1); // 기본값
  const [kLen, setKLen] = useState(0);
  const [mLen, setMLen] = useState(0);
  const [renderArray, setRenderArray] = useState<IClassificationProps[]>([]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [form, setForm, onChange] = useInputs([]);
  const { urlPartCode, urlProjectId } = useProject();
  const dispatch = useAppDispatch();
  
  // 자동 배치 실행 중 플래그 - 무한 루프 방지
  const isAutoFillingRef = useRef(false);
  // API 저장 실행 중 플래그 - 중복 호출 방지
  const isSavingRef = useRef(false);
  
  // 주기적으로 클릭 로그 전송 (30초마다) - 메인 컴포넌트에서도 처리하지만 중복 방지용
  useEffect(() => {
    if (!urlProjectId) return;

    const intervalId = setInterval(() => {
      // 비동기로 전송 (백그라운드에서 실행)
      inspectionLogger.flush().then((pendingLogs) => {
        if (pendingLogs.length > 0) {
          dispatch(postInspectionClickLogs({ projectId: urlProjectId, logs: pendingLogs }))
            .unwrap()
            .then(() => {
              console.log(`✅ 클릭 로그 ${pendingLogs.length}개 주기적 전송 완료`);
            })
            .catch((error) => {
              console.error('❌ 클릭 로그 주기적 전송 실패:', error);
            });
        }
      });
    }, 30 * 1000); // 30초

    return () => clearInterval(intervalId);
  }, [dispatch, urlProjectId]);

  const selectedInfo = useMemo(
    () => form.filter((obj: IFormProps) => obj.value === true).map((obj: IClassificationProps) => obj.id),
    [form],
  );

  // 우선순위 순서 정의
  const PRIORITY_ORDER = useMemo(
    () => [
      'AATempAA',
      'AATempA',
      'AATempN',
      'AATempNN',
      'ATempAA',
      'ATempA',
      'ATempN',
      'ATempNN',
      'NTempAA',
      'NTempA',
      'NTempN',
      'NTempNN',
      'NNTempAA',
      'NNTempA',
      'NNTempN',
      'NNTempNN',
    ],
    [],
  );

  // 순차 채우기 모드 감지 (모든 케이스에 적용)
  const isSequentialMode = useMemo(() => {
    // inspectionFinalBase에 항목이 있으면 순차 채우기 모드
    return (
      inspectionFinalBase.length > 0 && inspectionFinalBase.some((room: any) => room.array && room.array.length > 0)
    );
  }, [inspectionFinalBase]);

  // 초기화: 데이터가 로드되면 자동 채우기 시작
  useEffect(() => {
    if (inspectionFinalBase.length > 0 && Object.keys(inspectionEndClassification).length > 0) {
      // 자동 채우기 실행
      autoFillInspectionFinalBase();
    }
  }, [inspectionFinalBase.length, autoFillInspectionFinalBase]);

  // 자동 채우기 후 현재 방 찾기 및 화면 설정
  useEffect(() => {
    if (inspectionFinalBase.length === 0) return;

    // 마지막 방부터 역순으로 채워지지 않은 방 찾기
    let currentRoomIndex = -1;
    let currentRoomValue = 0;

    for (let i = inspectionFinalBase.length - 1; i >= 0; i--) {
      const room = inspectionFinalBase[i];
      if (room && room.value > 0) {
        const alreadyFilled = room.array?.length || 0;
        const needToFill = room.value - alreadyFilled;
        if (needToFill > 0) {
          currentRoomIndex = i + 1; // k는 1부터 시작
          currentRoomValue = needToFill;
          break;
        }
      }
    }

    if (currentRoomIndex > 0) {
      setK(currentRoomIndex);
      setKLen(currentRoomValue);
    } else {
      // 모든 방이 채워졌거나 inspectionEndClassification이 비어있음
      setK(0);
      setKLen(0);
    }
  }, [inspectionFinalBase, inspectionEndClassification]);

  useEffect(() => {
    // TODO: 새로 만들 예정
    // const tmp = inspectionFinalStandard.map((_obj: any) => {
    //   return {
    //     ..._obj,
    //     array: _obj.array.map((_oarray: any) => {
    //       return {
    //         ..._oarray,
    //         check: false,
    //       };
    //     }),
    //   };
    // });
    // changeInspectionFinalTmp(tmp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetFinalBase = useCallback(
    async (selectedArray: IClassificationProps[]) => {
      const baseItem = inspectionFinalBase[k - 1];
      if (!baseItem) {
        console.error('inspectionFinalBase item not found at index:', k - 1);
        return;
      }

      // 이미 다른 방에 들어간 항목들의 ID 수집
      const usedItemIds = new Set<string>();
      inspectionFinalBase.forEach((room, index) => {
        if (index !== k - 1 && room.array) {
          // 현재 방이 아닌 다른 방의 항목들
          room.array.forEach((item: IClassificationProps) => {
            usedItemIds.add(item.id);
          });
        }
      });

      // 선택된 항목들 중 이미 사용된 항목 제외
      const itemsToAdd = selectedArray.filter(
        (item: IClassificationProps) => !usedItemIds.has(item.id)
      ).slice(0, baseItem.value);
      
      // 현재 방의 기존 항목과 중복 제거
      const existingItems = baseItem.array || [];
      const existingIds = new Set(existingItems.map((item: IClassificationProps) => item.id));
      const newItems = itemsToAdd.filter(
        (item: IClassificationProps) => !existingIds.has(item.id)
      );
      
      const maxItems = baseItem.value;
      const finalItems = [...existingItems, ...newItems].slice(0, maxItems);

      await changeInspectionFinalBase({
        key: baseItem.key,
        value: baseItem.value,
        array: finalItems,
      });

      // inspectionEndClassification의 마지막 키에서 선택된 항목들 제거 (순서 정보 사용)
      // order가 큰 순서대로 정렬 (역순)
      const endClassificationKeys = Object.keys(inspectionEndClassification).sort((a, b) => {
        const aOrder = inspectionEndClassification[a]?.order ?? -1;
        const bOrder = inspectionEndClassification[b]?.order ?? -1;
        return bOrder - aOrder; // 큰 순서가 앞으로 (역순)
      });
      
      if (endClassificationKeys.length > 0) {
        // 정렬 후 첫 번째 키 선택 (가장 큰 order)
        const lastKey = endClassificationKeys[0];
        await removeItemsFromInspectionEndClassification(lastKey, itemsToAdd);
      }

      // 자동 채우기 실행 (다음 방 자동 처리)
      await autoFillInspectionFinalBase();
    },
    [inspectionFinalBase, k, inspectionEndClassification, changeInspectionFinalBase, removeItemsFromInspectionEndClassification, autoFillInspectionFinalBase],
  );

  // 현재 k의 왼쪽 방들로 남은 항목을 자동 분배
  const autoPlaceLeftoversToLeftRooms = useCallback(
    async (leftovers: IClassificationProps[]): Promise<string[]> => {
      const usedIds: string[] = [];
      const updates: { key: string; value: number; array: any[] }[] = [];
      let roomIdxZeroBased = k - 2; // k의 왼쪽 방부터
      while (leftovers.length > 0 && roomIdxZeroBased >= 0) {
        const room = inspectionFinalBase[roomIdxZeroBased];
        if (!room) break;
        const currentArr = Array.isArray(room.array) ? room.array : [];
        const capacity = Math.max(0, (room.value || 0) - currentArr.length);
        if (capacity > 0) {
          const takeCount = Math.min(capacity, leftovers.length);
          const toAssign = leftovers.splice(0, takeCount);
          usedIds.push(...toAssign.map((it: IClassificationProps) => it.id));
          updates.push({ key: room.key, value: room.value, array: [...currentArr, ...toAssign] });
        }
        roomIdxZeroBased -= 1;
      }
      // TODO: 새로 만들 예정
      // await Promise.all(updates.map(u => changeInspectionFinalBase({ key: u.key, value: u.value, array: u.array })));
      return usedIds;
    },
    [inspectionFinalBase, k],
  );

  // 여러 소스 방에서 필요한 개수만큼 항목을 수집하는 함수
  const collectItemsFromMultipleSources = useCallback(
    (startPriorityKey: string, neededCount: number): IClassificationProps[] => {
      // console.log('=== 여러 소스 방에서 항목 수집 ===');
      // console.log('시작 키:', startPriorityKey, '필요 개수:', neededCount);

      // 이미 사용된 항목들 수집
      const usedItemIds = new Set<string>();
      inspectionFinalBase.forEach((room: any) => {
        if (room.array) {
          room.array.forEach((item: IClassificationProps) => usedItemIds.add(item.id));
        }
      });

      const collectedItems: IClassificationProps[] = [];
      const startIndex = PRIORITY_ORDER.indexOf(startPriorityKey);

      if (startIndex === -1) {
        // console.log('❌ 시작 키를 우선순위에서 찾을 수 없음:', startPriorityKey);
        return [];
      }

      // 🔥 수정: 그룹 제한 없이 우선순위 순서대로 모든 방에서 수집
      // console.log('우선순위 순서대로 모든 방에서 수집 시작 (그룹 제한 없음)');

      // 시작 인덱스부터 끝까지 모든 키 검사
      const keysToCheck = PRIORITY_ORDER.slice(startIndex);

      // 각 키에서 순차적으로 항목 수집
      keysToCheck.forEach(currentKey => {
        if (collectedItems.length >= neededCount) return;

        const sourceRoom = inspectionFinalBase.find((room: any) => room.key === currentKey);
        if (sourceRoom && sourceRoom.array) {
          const availableItems = sourceRoom.array.filter((item: any) => {
            // 이미 사용되지 않은 항목
            return !usedItemIds.has(item.id);
          });

          if (availableItems.length > 0) {
            const takeCount = Math.min(availableItems.length, neededCount - collectedItems.length);
            const itemsToAdd = availableItems.slice(0, takeCount);
            collectedItems.push(...itemsToAdd);

            // console.log(`✅ ${currentKey}에서 ${itemsToAdd.length}개 수집 (전체 가능: ${availableItems.length}개)`);
            /* console.log(
              '  수집한 항목:',
              itemsToAdd.map(item => `${item.content}(${item.id.slice(-4)})`),
            );
            */
          } else {
            // console.log(`⏭️ ${currentKey}: 사용 가능한 항목 없음`);
          }
        }
      });

      // console.log('=== 수집 완료 ===');
      // console.log('총 수집된 항목:', collectedItems.length, '개 / 필요:', neededCount, '개');

      // 수집 가능한 모든 항목을 수집했는지 확인
      if (collectedItems.length < neededCount) {
        // console.log('⚠️ 필요한 개수보다 적게 수집됨 - 더 이상 수집할 항목이 없음');
      }

      return collectedItems;
    },
    [inspectionFinalBase, PRIORITY_ORDER],
  );

  const findBestSourceRoom = useCallback(
    (targetRoom: number): void => {
      // console.log('=== 우선순위 기반 소스 방 찾기 ===', '목표 방:', targetRoom);

      // 이미 사용된 항목들 수집
      const usedItemIds = new Set<string>();
      // TODO: 새로 만들 예정
      inspectionFinalBase.forEach((room: any) => {
        if (room.array) {
          room.array.forEach((item: any) => usedItemIds.add(item.id));
        }
      });
      // console.log('이미 사용된 항목 IDs:', Array.from(usedItemIds).map(id => id.slice(-4)));

      // 우선순위 순서대로 검사
      let foundSource = false;
      PRIORITY_ORDER.forEach(priorityKey => {
        if (foundSource) return;

        const sourceRoom = inspectionFinalBase.find((room: any) => room.key === priorityKey);
        if (sourceRoom && sourceRoom.array) {
          const availableItems = sourceRoom.array.filter((item: any) => !usedItemIds.has(item.id));

          if (availableItems.length > 0) {
            const sourceIndex = inspectionFinalBase.indexOf(sourceRoom) + 1;
            /* console.log(
              `✅ 우선순위 소스 방 발견: ${priorityKey} (m=${sourceIndex}, 사용가능=${actuallyAvailableItems.length}개)`,
            );
            console.log(
              '사용 가능한 항목들:',
              actuallyAvailableItems.map(item => `${item.content || item.id.slice(-4)}`),
            ); */

            setM(sourceIndex);
            setMLen(availableItems.length);
            foundSource = true;
            return;
          }
          // console.log(`❌ ${priorityKey}: 사용 가능한 항목 없음 (전체=${sourceRoom.array.length}개)`);
        } else {
          // console.log(`❌ ${priorityKey}: 방이 존재하지 않거나 비어있음`);
        }
      });

      if (!foundSource) {
        // console.log('❌ 모든 우선순위 방에서 사용 가능한 항목을 찾지 못함');
        setM(0);
        setMLen(0);
      }
    },
    [inspectionFinalBase, PRIORITY_ORDER],
  );

  const determineNextStep = useCallback(async () => {
    // console.log('=== 다음 단계 결정 (오른쪽부터) ===');
    // console.log('현재 inspectionFinalBase 상태:');
    // TODO: 새로 만들 예정
    inspectionFinalBase.forEach((room: any, index: number) => {
      // console.log(`  방 ${index}: key="${room.key}", value=${room.value}, 현재=${room.array?.length || 0}개`);
    });

    // 모든 방이 채워졌는지 확인 (오른쪽부터 검사 - k가 큰 수부터 채우기)
    let allRoomsFilled = true;
    let nextEmptyRoom = -1;

    for (let i = inspectionFinalBase.length - 1; i >= 0; i--) {
      const room = inspectionFinalBase[i];
      const currentCount = room.array?.length || 0;
      const requiredCount = room.value || 0;
      /* if (k === 3 || k === 11) {
        console.log(
          `방 ${i + 1} (key="${room.key}"): ${currentCount}/${requiredCount} - ${requiredCount > 0 && currentCount < requiredCount ? '빈 방' : '완료됨'}`,
        );
      } */
      if (requiredCount > 0 && currentCount < requiredCount) {
        allRoomsFilled = false;
        if (nextEmptyRoom === -1) {
          nextEmptyRoom = i + 1; // k는 1부터 시작
          /* if (k === 3 || k === 11) {
            console.log(
              `🎯 다음 빈 방 발견: ${nextEmptyRoom} (key="${room.key}", 필요: ${requiredCount}개, 현재: ${currentCount}개)`,
            );
          } */
        }
      }
    }

    console.log('=== allRoomsFilled 확인 ===');
    console.log('allRoomsFilled:', allRoomsFilled);
    console.log('surveyAdd.length:', surveyAdd.length);
    
    if (allRoomsFilled) {
      // console.log('모든 방이 채워짐! 완료 처리');
      // console.log('=== 완료 처리 시작 ===');
      // console.log('surveyAdd.length:', surveyAdd.length);

      if (surveyAdd.length > 0) {
        // console.log('📋 추가 설문이 있음 - additional 화면으로 이동');
        await changeClassification('additional');
      } else {
        // console.log('✅ 추가 설문 없음 - 완료 처리 및 저장');
        
        // 중복 호출 방지
        if (isSavingRef.current) {
          return;
        }
        
        isSavingRef.current = true;
        
        await changeClassification('complete');

        // API 저장 호출
        // 중복 확인
        // TODO: 새로 만들 예정
        // value > 0인 방에서만 항목을 수집하고, array가 존재하는지 확인
        const allIds = inspectionFinalBase
          .filter((obj: any) => obj.value > 0 && obj.array && Array.isArray(obj.array) && obj.array.length > 0)
          .flatMap((obj: any) => {
            return obj.array
              .map((obj2: any) => obj2?.id)
              .filter((id: any) => id != null && id !== undefined && id !== '');
          });
        
        try {
          await dispatch(openBackdrop({}));
          
          // 클릭 로그 전송 (비동기로 처리)
          if (urlProjectId) {
            inspectionLogger.flushAll().then((pendingLogs) => {
              if (pendingLogs.length > 0) {
                dispatch(postInspectionClickLogs({ projectId: urlProjectId, logs: pendingLogs }))
                  .unwrap()
                  .then(() => {
                    console.log(`✅ 클릭 로그 ${pendingLogs.length}개 최종 전송 완료`);
                  })
                  .catch((logError) => {
                    console.error('❌ 클릭 로그 저장 실패:', logError);
                    // 로그 저장 실패는 무시하고 계속 진행
                  });
              }
            });
          }
          
          await dispatch(
            postInspectionResult({
              projectId: urlProjectId || '',
              partCode: urlPartCode || '',
              inspectionId: inspectionDetail.id,
              ipsativeIdResultList: allIds,
              surveyAddResultList: [],
            }),
          ).unwrap();

          // console.log('✅ API 저장 성공');
          dispatch(showMessage({ message: '설문이 완료되었습니다.', variant: 'success' }));
        } catch (error) {
          console.error('❌ API 저장 실패:', error);
          dispatch(showMessage({ message: '저장 중 오류가 발생했습니다.', variant: 'error' }));
        } finally {
          dispatch(closeBackdrop());
          isSavingRef.current = false;
        }
      }
      return;
    }

    if (nextEmptyRoom !== -1) {
      // if (k === 3 || nextEmptyRoom === 3) {
      //   console.log('🔄 다음 단계로 이동:', nextEmptyRoom);
      // }
      setK(nextEmptyRoom);
      setKLen(inspectionFinalBase[nextEmptyRoom - 1]?.value || 0);
      findBestSourceRoom(nextEmptyRoom);
    } else {
      // if (k === 3) console.log('다음에 채울 방을 찾지 못함');
    }
  }, [
    inspectionFinalBase,
    surveyAdd,
    changeClassification,
    findBestSourceRoom,
    dispatch,
    inspectionDetail.id,
    urlPartCode,
    urlProjectId,
  ]);

  // 현재 방에 대한 선택 화면 설정
  useEffect(() => {
    if (k === 0 || inspectionFinalBase.length === 0) {
      setRenderArray([]);
      return;
    }

    const baseItem = inspectionFinalBase[k - 1];
    if (!baseItem) {
      setRenderArray([]);
      return;
    }

    const requiredCount = baseItem.value;
    const alreadyFilled = baseItem.array?.length || 0;
    const needToFill = requiredCount - alreadyFilled;

    if (needToFill <= 0) {
      // 이미 채워진 방이면 다음 방으로 자동 진행
      autoFillInspectionFinalBase();
      setRenderArray([]);
      return;
    }

    // inspectionEndClassification의 마지막 키 찾기 (순서 정보 사용)
    // order가 큰 순서대로 정렬 (역순)
    const endClassificationKeys = Object.keys(inspectionEndClassification).sort((a, b) => {
      const aOrder = inspectionEndClassification[a]?.order ?? -1;
      const bOrder = inspectionEndClassification[b]?.order ?? -1;
      return bOrder - aOrder; // 큰 순서가 앞으로 (역순)
    });
    
    if (endClassificationKeys.length === 0) {
      // 더 이상 채울 항목이 없음
      setRenderArray([]);
      return;
    }

    // 이미 inspectionFinalBase의 모든 방에 들어간 항목들의 ID 수집
    const usedItemIds = new Set<string>();
    inspectionFinalBase.forEach((room) => {
      if (room.array) {
        room.array.forEach((item: IClassificationProps) => {
          usedItemIds.add(item.id);
        });
      }
    });

    // 정렬 후 첫 번째 키 선택 (가장 큰 order)
    const lastKey = endClassificationKeys[0];
    const lastGroup = inspectionEndClassification[lastKey];
    if (!lastGroup) {
      setRenderArray([]);
      return;
    }
    
    let lastItems = lastGroup.items || [];
    
    // 이미 사용된 항목 제외
    lastItems = lastItems.filter((item: IClassificationProps) => !usedItemIds.has(item.id));

    if (lastItems.length === 0) {
      // 사용 가능한 항목이 없으면 자동 채우기 실행
      autoFillInspectionFinalBase();
      setRenderArray([]);
      return;
    }

    // 항목 개수가 필요한 개수보다 많으면 화면에 보여주고 선택하게 함
    if (lastItems.length > needToFill) {
      setRenderArray(lastItems);
    } else {
      // 항목 개수가 필요한 개수 이하이면 자동으로 모두 넣기
      // 자동 채우기 실행
      autoFillInspectionFinalBase();
      setRenderArray([]);
    }
  }, [k, inspectionFinalBase, inspectionEndClassification, autoFillInspectionFinalBase]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleResultInspectionSave = useCallback(async () => {
    // value > 0인 방에서만 항목을 수집하고, array가 존재하는지 확인
    const allIds = inspectionFinalBase
      .filter((obj: any) => obj.value > 0 && obj.array && Array.isArray(obj.array) && obj.array.length > 0)
      .flatMap((obj: any) => {
        return obj.array
          .map((obj2: any) => obj2?.id)
          .filter((id: any) => id != null && id !== undefined && id !== '');
      });
    
    dispatch(openBackdrop({}));
    await dispatch(
      postInspectionResult({
        projectId: urlProjectId || '',
        partCode: urlPartCode || '',
        inspectionId: inspectionDetail.id,
        // TODO: 새로 만들 예정
        // value > 0인 방에서만 항목을 수집하고, array가 존재하는지 확인
        ipsativeIdResultList: allIds,
        surveyAddResultList: [],
      }),
    )
      .unwrap()
      .then((response: { status: number; data: { code: string; message: string } }) => {
        const { status } = response;
        if (status === 200) {
          dispatch(showMessage({ message: response?.data?.message || '성공했습니다.', variant: 'success' }));
        } else {
          dispatch(showMessage({ message: response?.data?.message || '실패했습니다.', variant: 'warning' }));
        }
      })
      .catch((e: any) => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    inspectionDetail.id,
    inspectionFinalBase,
    postInspectionResult,
    showMessage,
    closeBackdrop,
    urlPartCode,
    urlProjectId,
  ]);

  const handleClearArrays = () => {
    // console.log('=== 초기화 실행 ===');
    // console.log('초기화 전 inspectionFinalBase:', inspectionFinalBase);
    // TODO: 새로 만들 예정
    // clearInspectionFinalBaseArrays();
    setRenderArray([]);
    // console.log('✅ inspectionFinalBase 배열 초기화 완료');
    dispatch(showMessage({ message: '모든 방의 항목이 초기화되었습니다.', variant: 'info' }));
  };

  const handleNext = useCallback(async () => {
    if (k === 0) return;
    
    const baseItem = inspectionFinalBase[k - 1];
    if (!baseItem) return;

    const requiredCount = baseItem.value;
    const alreadyFilled = baseItem.array?.length || 0;
    const needToFill = requiredCount - alreadyFilled;

    if (needToFill <= 0) {
      // 이미 채워진 방이면 자동 채우기 실행
      autoFillInspectionFinalBase();
      return;
    }

    // 선택된 항목들 가져오기
    const selectedItems = renderArray.filter((item: IClassificationProps) => 
      selectedInfo.includes(item.id)
    );

    if (selectedItems.length !== needToFill) {
      // 필요한 개수만큼 선택되지 않았으면 중단
      return;
    }

    // 최종 단계 다음 버튼 클릭 이벤트 로깅 및 비동기 전송
    if (urlProjectId && urlPartCode && inspectionDetail.id) {
      inspectionLogger.logClick({
        projectId: urlProjectId,
        partCode: urlPartCode,
        inspectionId: inspectionDetail.id,
        step: 'final' as const,
        stepName: getStepName('final'),
        actionType: 'next_button',
        selectedCount: selectedItems.length,
        totalAvailable: renderArray.length,
      });

      // 비동기로 전송 (백그라운드에서 실행)
      inspectionLogger.flush().then((pendingLogs) => {
        if (pendingLogs.length > 0) {
          dispatch(postInspectionClickLogs({ projectId: urlProjectId, logs: pendingLogs }))
            .unwrap()
            .then(() => {
              console.log(`✅ 클릭 로그 ${pendingLogs.length}개 전송 완료`);
            })
            .catch((error) => {
              console.error('❌ 클릭 로그 전송 실패:', error);
            });
        }
      });
    }

    // 선택된 항목들을 현재 방에 넣기
    await handleSetFinalBase(selectedItems);
  }, [k, inspectionFinalBase, renderArray, selectedInfo, autoFillInspectionFinalBase, handleSetFinalBase, urlProjectId, urlPartCode, inspectionDetail.id]);

  useEffect(() => {
    const newForm = renderArray.map(obj => {
      return {
        id: obj.id,
        value: false, // 사용자가 직접 선택하도록 초기값 false
      } as IFormProps;
    });
    // console.log('=== Form Update ===');
    // console.log('Setting form from renderArray:', newForm);
    setForm(newForm);
  }, [renderArray, setForm]);

  useEffect(() => {
    const baseItem = inspectionFinalBase[k - 1];
    const requiredCount = baseItem?.value || 0;
    const alreadyInRoom = baseItem?.array?.length || 0;
    const needToSelect = Math.max(0, requiredCount - alreadyInRoom);
    const isFormDisabled = selectedInfo.length !== needToSelect;

    // console.log('=== isDisabled 계산 ===');
    // console.log('requiredCount:', requiredCount);
    // console.log('alreadyInRoom:', alreadyInRoom);
    // console.log('needToSelect:', needToSelect);
    // console.log('selectedInfo.length:', selectedInfo.length);
    // console.log('isDisabled:', isFormDisabled);

    setIsDisabled(isFormDisabled);
  }, [inspectionFinalBase, k, selectedInfo.length]);

  const preCheckedLength = renderArray.filter(obj => obj?.essential === true)?.length;

  // console.log('=== Render Debug ===');
  // console.log('renderArray:', renderArray);
  // console.log('renderArray.length:', renderArray.length);
  // console.log('preCheckedLength:', preCheckedLength);
  // console.log('form:', form);
  // console.log('selectedInfo:', selectedInfo);
  // console.log('k:', k, 'kLen:', kLen, 'm:', m, 'mLen:', mLen);

  // 선택 개수 계산 디버깅
  const currentRoom = inspectionFinalBase[k - 1];
  const requiredCount = currentRoom?.value || 0;
  const alreadySelected = currentRoom?.array?.length || 0;
  const needToSelect = Math.max(0, requiredCount - alreadySelected);
  const hasEnoughItems = alreadySelected >= requiredCount;

  // console.log('=== Selection Count Debug ===');
  // console.log('k:', k, `-> currentRoom[${k - 1}]:`, currentRoom);
  // console.log('requiredCount:', requiredCount);
  // console.log('alreadySelected:', alreadySelected);
  // console.log('needToSelect:', needToSelect);
  // console.log('hasEnoughItems:', hasEnoughItems);

  // 렌더링 조건 체크 - 사용자가 선택해야 하는 경우
  // renderArray에 항목이 있으면 화면 표시 (선택이 필요한 경우)
  const shouldRender = renderArray.length > 0 && needToSelect > 0;
  // console.log('=== Rendering Decision ===');
  // console.log('renderArray.length:', renderArray.length);
  // console.log('needToSelect:', needToSelect);
  // console.log('shouldRender (화면 표시):', shouldRender, `(${renderArray.length} > ${needToSelect})`);
  // console.log('Final shouldRender:', shouldRender);
  useEffect(() => {
    const falseCount = form.filter((obj: IFormProps) => obj.value === false).length;
    const trueCount = form.filter((obj: IFormProps) => obj.value === true).length;

    // 자동 진행 조건을 더 엄격하게 설정 - 실제로 선택할 항목이 없을 때만
    const autoNeedToSelect = Math.max(0, kLen - preCheckedLength);
    // 자동 진행 조건 - 오직 필요한 경우만
    const noItemsToSelect = renderArray.length === 0; // 선택할 항목이 없음
    const onlyOneItemToSelect = renderArray.length === 1 && needToSelect === 1; // 1개만 있고 1개만 선택하면 됨
    // onlyOneItemToSelect는 자동 진행에서 제외 - 사용자가 직접 선택하도록
    const autoProgressCondition = false;

    // console.log('=== Auto Progress Condition Analysis ===');
    // console.log('hasEnoughItems:', hasEnoughItems, `(${preCheckedLength} >= ${kLen})`);
    // console.log('noItemsToSelect:', noItemsToSelect, `(renderArray.length = ${renderArray.length})`);
    // console.log('onlyOneItemToSelect:', onlyOneItemToSelect, `(${renderArray.length} === 1 && ${needToSelect} === 1)`);
    // console.log('Final autoProgressCondition:', autoProgressCondition);

    // console.log('=== Auto Progress Check ===');
    // console.log('kLen:', kLen);
    // console.log('preCheckedLength:', preCheckedLength);
    // console.log('autoNeedToSelect:', autoNeedToSelect);
    // console.log('renderArray.length:', renderArray.length);
    // console.log('falseCount:', falseCount);
    // console.log('trueCount:', trueCount);
    // console.log('autoProgressCondition:', autoProgressCondition);
    // console.log('Should show selection UI:', !autoProgressCondition && renderArray.length > 0);

    // 🛑 자동 진행 완전 비활성화 - 사용자가 직접 선택하도록
    if (false && autoProgressCondition) {
      // console.log('🚫 Auto progress is COMPLETELY DISABLED');
      // console.log('User must manually select items');
    }

    // 디버깅용 로그
    if (autoProgressCondition) {
      // console.log('⚠️ Auto progress condition is TRUE but DISABLED');
      // console.log('hasEnoughItems:', hasEnoughItems);
      // console.log('noItemsToSelect:', noItemsToSelect);
      // console.log('User must select manually');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, kLen, preCheckedLength, renderArray]);
  /* console.log(
    'max',
    kLen,
    preCheckedLength,
    kLen === preCheckedLength &&
      kLen - preCheckedLength === 0 &&
      form.filter((obj: IFormProps) => obj.value === false).length === 0 &&
      form.filter((obj: IFormProps) => obj.value === true).length === kLen,
  ); */

  // 자동 배치 로직: 선택지가 필요한 개수 이하이면 자동으로 채우기
  useEffect(() => {
    // 이미 자동 배치 실행 중이면 중단
    if (isAutoFillingRef.current) {
      console.log('⏸️ 자동 배치 이미 실행 중 - 스킵');
      return;
    }

    const baseItem = inspectionFinalBase[k - 1];
    const required = baseItem?.value || 0;
    const selectedCount = baseItem?.array?.length || 0;
    const need = Math.max(0, required - selectedCount);

    if (k === 3) {
      console.log('=== Auto-Fill Check (k=3) ===');
      console.log(
        '현재 방:',
        `k=${k} (key="${baseItem?.key}")`,
        '필요:',
        required,
        '현재:',
        selectedCount,
        '부족:',
        need,
      );
      console.log('후보 항목:', renderArray.length, '개');
      console.log('isAutoFillingRef:', isAutoFillingRef.current);
    }

    if (renderArray.length > 0) {
      /* console.log(
        '후보 항목들:',
        renderArray.map(item => `${item.content}(${item.id.slice(-4)})`),
      ); */
    }

    // renderArray가 비어있으면 재수집 또는 다음 단계 확인
    if (renderArray.length === 0) {
      if (need === 0) {
        if (k === 3) console.log('✅ 방이 채워짐 - 다음 단계로');
        (async () => {
          await determineNextStep();
        })();
      } else {
        if (k === 3) console.log('⏳ renderArray 비어있음 - 재수집 대기 중 (need:', need, ')');
      }
      return;
    }

    // 🔥 핵심 로직: 
    // 1) 후보 > 필요: 사용자 선택 (예: 3개 중 2개 선택, 4개 중 1개 선택)
    // 2) 후보 === 필요: 자동 배치 (예: 2개 있고 2개 필요)
    // 3) 후보 < 필요: 자동 배치 (예: 1개 있고 4개 필요)
    const shouldAutoFill = renderArray.length === need || renderArray.length < need;
    
    if (k === 3) {
      console.log('🔍 배치 조건 체크 (k=3):');
      console.log('  renderArray.length:', renderArray.length);
      console.log('  need:', need);
      console.log('  renderArray.length === need:', renderArray.length === need);
      console.log('  renderArray.length < need:', renderArray.length < need);
      console.log('  shouldAutoFill:', shouldAutoFill);
      console.log('  renderArray:', renderArray.map(item => item.content || item.id.slice(-4)));
    }
    
    if (need > 0 && renderArray.length > 0 && shouldAutoFill) {
      // 선택지가 필요한 개수와 같거나 적을 때 자동 배치
      if (k === 3) console.log('✅ 자동 배치 실행: 후보', renderArray.length, '개, 필요', need, '개');
      const selectedAll = renderArray.map(obj => ({ ...obj, check: true }));
      
      // 플래그 설정 - 자동 배치 시작
      isAutoFillingRef.current = true;
      
      // 즉시 renderArray를 비워서 중복 실행 방지
      setRenderArray([]);
      
      // 비동기 작업 수행
      (async () => {
        if (k === 3) console.log('🚀 자동 배치 실행 중... (k=3)');
        await handleSetFinalBase(selectedAll as any);

        // 여러 소스 방에서 체크 표시
        // TODO: 새로 만들 예정
        const usedIdSet = new Set<string>(selectedAll.map((s: any) => s.id));
        await Promise.all(
          inspectionFinalTmp.map(async (tmpRoom: any) => {
            if (tmpRoom.array) {
              let hasChanges = false;
              const updatedArray = tmpRoom.array.map((item: any) => {
                if (usedIdSet.has(item.id) && !item.check) {
                  hasChanges = true;
                  return { ...item, check: true };
                }
                return item;
              });

              if (hasChanges) {
                // await changeInspectionFinalTmpObject({
                //   key: tmpRoom.key,
                //   value: tmpRoom.value,
                //   array: updatedArray,
                // });
              }
            }
          }),
        );

        // 아직 더 채워야 하는지 확인
        const updatedBaseItem = inspectionFinalBase[k - 1];
        const updatedSelectedCount = (updatedBaseItem?.array?.length || 0) + selectedAll.length;
        const stillNeed = Math.max(0, required - updatedSelectedCount);

        console.log('🔍 자동 배치 후 상태:', { 필요: required, 현재: updatedSelectedCount, 아직필요: stillNeed });

        if (stillNeed > 0) {
          console.log(`⏭️ 아직 ${stillNeed}개 더 필요 - 즉시 수집/저장 루프로 처리 (renderArray 미사용)`);
          let remaining = stillNeed;
          const currentTmpRoom = inspectionFinalBase[m - 1];
          const startKey = currentTmpRoom?.key || 'AATempAA';
          console.log('수집 시작 키:', startKey);
          while (remaining > 0) {
            const batch = collectItemsFromMultipleSources(startKey, remaining);
            console.log(`📦 배치 수집: ${batch.length}개 (남은 필요: ${remaining}개)`);
            if (batch.length === 0) {
              console.log('❌ 더 이상 수집할 항목이 없음');
              break;
            }
            await handleSetFinalBase(batch as any);

            // tmp 체크 표시
            // TODO: 새로 만들 예정
            const usedIdSet = new Set<string>(batch.map((s: any) => s.id));
            await Promise.all(
              inspectionFinalTmp.map(async (tmpRoom: any) => {
                if (!tmpRoom.array) return;
                let hasChanges = false;
                const updatedArray = tmpRoom.array.map((item: any) => {
                  if (usedIdSet.has(item.id) && !item.check) {
                    hasChanges = true;
                    return { ...item, check: true };
                  }
                  return item;
                });
                if (hasChanges) {
                  // await changeInspectionFinalTmpObject({ key: tmpRoom.key, value: tmpRoom.value, array: updatedArray });
                }
              }),
            );

            remaining -= batch.length;
          }

          if (remaining > 0) {
            console.log(`⚠️ 수집 불가로 ${remaining}개 부족 - 다음 단계 진행`);
          } else {
            console.log('✅ 필요한 개수 모두 채움');
          }
          console.log('🏁 자동 배치 완료 - 다음 단계로');
          await determineNextStep();
          // 플래그 해제
          isAutoFillingRef.current = false;
          return;
        }
        console.log('✅ 방이 모두 채워짐 - 다음 방으로 이동');
        await determineNextStep();
        // 플래그 해제
        isAutoFillingRef.current = false;
      })();
      
      // 중요: 이 조건이 다시 true가 되지 않도록 renderArray는 이미 비워짐
      return;
    } else if (renderArray.length > need && need > 0) {
      if (k === 3) console.log('👤 사용자 선택 필요 (k=3):', `후보 ${renderArray.length}개 > 필요 ${need}개`);
    } else if (need === 0) {
      if (k === 3) console.log('⚠️ 이미 채워진 방 - 다음 단계로 (k=3)');
      (async () => {
        await determineNextStep();
      })();
    }
  }, [
    // renderArray.length를 의존성에서 제거하여 무한 루프 방지
    // 대신 needToSelect를 계산하는 데 사용되는 값들만 의존성으로 사용
    k,
    kLen,
    inspectionFinalBase,
  ]);

  // renderArray 변경 추적
  useEffect(() => {
    console.log('🔄 renderArray 변경됨:', renderArray.length, '개');
    if (renderArray.length > 0) {
      console.log('항목들:', renderArray.map(item => item.content || item.id.slice(-4)));
    }
  }, [renderArray]);

  // 현재 방이 이미 채워졌고 선택할 항목이 없으면 강제로 다음 단계로
  useEffect(() => {
    const baseItem = inspectionFinalBase[k - 1];
    const required = baseItem?.value || 0;
    const selectedCount = baseItem?.array?.length || 0;
    const currentNeedToSelect = Math.max(0, required - selectedCount);

    // console.log('=== 강제 진행 체크 ===');
    // console.log('k:', k, 'currentNeedToSelect:', currentNeedToSelect, 'renderArray.length:', renderArray.length);

    if (currentNeedToSelect === 0 && renderArray.length === 0) {
      // console.log('🚀 강제 다음 단계 진행 - 현재 방 완료됨');
      setTimeout(() => {
        determineNextStep();
      }, 100);
    }
  }, [k, inspectionFinalBase, renderArray.length, determineNextStep]);

  // k 값이 변경될 때 renderArray 강제 업데이트
  useEffect(() => {
    // console.log('=== k 값 변경 감지 ===');
    // console.log('새로운 k:', k, 'kLen:', kLen);
    // console.log('새로운 m:', m, 'mLen:', mLen);
    // console.log('inspectionFinalBase[k-1]:', inspectionFinalBase[k - 1]);

    // 현재 방이 이미 모두 채워졌는데 renderArray가 있으면 초기화
    const baseItem = inspectionFinalBase[k - 1];
    const requiredCount = baseItem?.value || 0;
    const alreadySelected = baseItem?.array?.length || 0;
    const currentNeedToSelect = Math.max(0, requiredCount - alreadySelected);
    
    if (currentNeedToSelect === 0 && renderArray.length > 0) {
      // console.log('⚠️ 방이 이미 채워졌는데 renderArray가 있음 - 초기화하고 다음 단계로');
      setRenderArray([]);
      // setTimeout으로 감싸서 무한 루프 방지
      setTimeout(() => {
        determineNextStep();
      }, 100);
      return;
    }

    if (k > 0 && m > 0 && inspectionFinalBase.length > 0) {
      const tmpItem = inspectionFinalBase[m - 1];
      if (tmpItem && tmpItem.array) {
        // 이미 사용된 항목 제외
        const usedItemIds = new Set();
        inspectionFinalBase.forEach((room: any) => {
          if (room.array) {
            room.array.forEach((item: any) => usedItemIds.add(item.id));
          }
        });
        const availableItems = tmpItem.array.filter((item: any) => !usedItemIds.has(item.id));
        // console.log('강제 업데이트 - 사용 가능한 항목:', availableItems.length, '개');
        /* console.log(
          '강제 업데이트 - 항목들:',
          availableItems.map(item => item.content),
        ); */

        // console.log('강제 업데이트 - 실제 사용 가능:', availableItems.length, '개');

        // renderArray.length를 의존성에서 제거하고, ref나 이전 값과 비교
        setRenderArray(prevArray => {
          // 길이가 다르거나 내용이 다르면 업데이트
          if (prevArray.length !== availableItems.length) {
            // console.log('🔄 renderArray 강제 업데이트 실행');
            return [...availableItems];
          }
          // 길이는 같지만 내용이 다를 수 있으므로 비교
          const prevIds = new Set(prevArray.map(item => item.id));
          const newIds = new Set(availableItems.map(item => item.id));
          if (prevIds.size !== newIds.size || ![...prevIds].every(id => newIds.has(id))) {
            // console.log('🔄 renderArray 내용 변경 감지 - 업데이트 실행');
            return [...availableItems];
          }
          return prevArray; // 변경 없으면 이전 값 반환
        });
        
        if (availableItems.length === 0) {
          // console.log('🔍 현재 소스 방에 사용 가능한 항목이 없음 - 다른 소스 방 찾기');
          // 다른 소스 방 찾기 시도
          findBestSourceRoom(k);
        }
      }
    }
  }, [findBestSourceRoom, inspectionFinalBase, k, kLen, m, mLen, determineNextStep]);

  if (k === 3) {
    console.log('=== 최종 렌더링 결정 (k=3) ===');
    console.log('shouldRender:', shouldRender);
    console.log('renderArray.length:', renderArray.length);
    console.log('needToSelect:', needToSelect);
    console.log('k:', k, 'kLen:', kLen);
  }
  
  if (!shouldRender) {
    if (k === 3) {
      console.log('❌ UI 렌더링 안 함! (k=3)');
      console.log('이유 - renderArray.length:', renderArray.length);
      console.log('이유 - needToSelect:', needToSelect);
      console.log('조건:', `${renderArray.length} > ${needToSelect} && ${needToSelect} > 0 = ${shouldRender}`);
      console.log('현재 방 상태:', inspectionFinalBase[k-1]);
    }
    return null;
  }

  if (k === 3) console.log('✅ UI 렌더링 - 모든 조건 만족 (k=3)');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Box
        sx={{
          p: 4,
          width: '100%',
          maxWidth: { xs: '100%', md: '1536px' },
          height: { xs: 'auto', md: '864px' },
        }}
      >
        <Container maxWidth="xl" sx={{ height: '100%', overflow: 'hidden' }}>
          <Stack
            direction="column"
            justifyContent="flex-start"
            alignContent="center"
            alignItems="center"
            sx={{ height: '100%', overflow: 'hidden' }}
          >
            <Box typography="s32w400" sx={{ mb: 3, flexShrink: 0 }}>
              다음 문장 중 가장 동의하는 문장 {needToSelect || 1}개를 선택해주세요.
            </Box>
            <Container maxWidth="lg" sx={{ p: 0, flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <CheckGroup list={renderArray} onChange={onChange} form={form} />
            </Container>
          </Stack>
        </Container>
      </Box>
      {/* 하단 버튼 영역 높이만큼 여백 추가 (버튼 높이 52px + 패딩 32px = 약 84px) */}
      <Box sx={{ height: { xs: 84, sm: 84 } }} />
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          bgcolor: 'background.paper',
          boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
          py: 2,
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {/* <PositiveButton
          variant="outlined"
          onClick={handleClearArrays}
          sx={{ width: { xs: 130 }, height: 52, typography: 's14w500', bgcolor: '#ef5350', color: 'white' }}
          title="🔄 초기화"
        /> */}
        <PositiveButton
          variant="contained"
          onClick={handleNext}
          disabled={isDisabled}
          sx={{ width: { xs: 130 }, height: 52, typography: 's14w500' }}
          title="다음"
        />
      </Box>
    </Box>
  );
}

export default memo(ClassificationFinalCheckN);
